import React, { useEffect, useState } from 'react';
import { getInitials } from '@gettactic/helpers';
import { SpotLayout } from '../../types';

const globalImageCache: Record<string, string> = {};

const extractUserId = (url: string): string | null => {
  const userIdPattern =
    /profile-photos\/(user_[a-f0-9\-]{36}\/\/\d{13})\.jpeg/i;
  const match = decodeURIComponent(url).match(userIdPattern);
  return match ? match[1] : null;
};

export type CircleProfilePhotoSVGProps = {
  profilePhoto: string;
  name: string;
  restricted?: boolean;
  privacy?: boolean;
  layout: SpotLayout;
  radius: number;
};

export const CircleProfilePhotoSVG: React.FC<CircleProfilePhotoSVGProps> = ({
  profilePhoto,
  name,
  restricted = false,
  privacy = false,
  layout,
  radius: circleRadius
}) => {
  const [base64Image, setBase64Image] = useState<string | null>(null);

  useEffect(() => {
    if (!profilePhoto || privacy) return;

    // Check global cache
    if (globalImageCache[profilePhoto]) {
      setBase64Image(globalImageCache[profilePhoto]);
      return;
    }

    const img = new window.Image();

    img.onload = async () => {
      try {
        const response = await fetch(img.src);
        const blob = await response.blob();

        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          globalImageCache[profilePhoto] = dataUrl;
          setBase64Image(dataUrl);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('CORS prevents converting image to base64', error);
      }
    };

    img.onerror = () => console.error('Failed to load image:', profilePhoto);

    img.crossOrigin = 'Anonymous';
    img.src = profilePhoto.startsWith('https://cdn.gettactic.com/image-crop/')
      ? `http://gondor.gettactic.loc:3000/api/profile-picture?userId=${encodeURIComponent(extractUserId(profilePhoto) || '')}`
      : profilePhoto;
  }, [profilePhoto, privacy]);

  if (privacy) return null;

  const initials = getInitials(name || '');

  // Calculate font size
  const maxFontSize = circleRadius * 0.5;
  const textWidth = circleRadius * 1.5;
  const textHeight = circleRadius * 1.5;

  const x = layout.circle.x - circleRadius;
  const y = layout.circle.y - circleRadius;
  const width = circleRadius * 2;
  const height = circleRadius * 2;

  return (
    <g
      transform={`translate(${x}, ${y})`}
      opacity={restricted ? 0.5 : 1}
    >
      <defs>
        <clipPath id={`clip-${layout.circle.x}-${layout.circle.y}`}>
          <circle
            cx={circleRadius}
            cy={circleRadius}
            r={circleRadius}
          />
        </clipPath>
      </defs>
      {base64Image ? (
        <image
          href={base64Image}
          width={width}
          height={height}
          clipPath={`url(#clip-${layout.circle.x}-${layout.circle.y})`}
        />
      ) : (
        <>
          <circle
            cx={circleRadius}
            cy={circleRadius}
            r={circleRadius}
            fill="#223C5B"
            strokeWidth={0}
          />
          <text
            x={circleRadius}
            y={circleRadius}
            fontSize={maxFontSize}
            fontStyle="bold"
            fill="white"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {initials}
          </text>
        </>
      )}
    </g>
  );
}; 